import React, { useMemo, useState } from "react";
import { WORDS, unlockedWords } from "../lib/tokiPonaData";
import {
  ROLES,
  buildRoleRound,
  rolesForWord,
  rouletteWords,
} from "../lib/tokiPonaRoles";
import { Html, Icon, SpeakButton } from "./tokiPonaUi";

const ROUND_LENGTH = 10;

// Renders a toki pona sentence with the target word lit up. `speak` is
// withheld on unanswered choices so a tap always registers as an answer.
function Sentence({ tp, word, speak = true }) {
  return (
    <div className="role-sentence">
      {tp.split(" ").map((tok, i) => {
        const bare = tok.replace(/\?$/, "");
        return (
          <span key={i} className={bare === word ? "role-hit" : ""}>
            {tok}
          </span>
        );
      })}
      {speak && <SpeakButton text={tp} />}
    </div>
  );
}

export default function TokiPonaRoles({ completed, onAward }) {
  // Only words unlocked through lessons — the same pool as the games. The
  // home screen gates this mode until units 1–2 are done, so the pool is
  // always big enough by the time anyone gets here.
  const available = useMemo(
    () => unlockedWords(completed || {}),
    [completed]
  );

  const poolSize = useMemo(() => rouletteWords(available).length, [available]);

  const [round, setRound] = useState(null); // array of questions
  const [qi, setQi] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selection, setSelection] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  const start = () => {
    setRound(buildRoleRound(available, { count: ROUND_LENGTH }));
    setQi(0);
    setAnswered(false);
    setSelection(null);
    setCorrectCount(0);
    setFinished(false);
  };

  const q = round && round[qi];

  const choose = (value, isCorrect) => {
    if (answered) return;
    setSelection(value);
    setAnswered(true);
    if (isCorrect) setCorrectCount((c) => c + 1);
  };

  const advance = () => {
    if (qi + 1 >= round.length) {
      const bonus = correctCount === round.length ? 4 : 0;
      if (onAward) onAward(Math.floor(correctCount / 2) + bonus);
      setFinished(true);
    } else {
      setQi(qi + 1);
      setAnswered(false);
      setSelection(null);
    }
  };

  // ---- intro ---------------------------------------------------------------
  if (!round) {
    return (
      <div className="roles-lab">
        <div className="roles-intro">
          <h2>one word, many hats</h2>
          <p>
            toki pona words refuse to stay in one lane — <i>moku</i> is food,
            eating, and edible all at once. Each spin deals you {ROUND_LENGTH}{" "}
            sentences: figure out which hat the glowing word is wearing, or
            hunt down the sentence where it wears the hat you're asked for.
          </p>
          <div className="roles-hats">
            {Object.entries(ROLES).map(([key, r]) => (
              <div className="roles-hat" key={key}>
                <span className={`role-badge role-${key}`}>{r.label}</span>
                <span className="roles-hat-def">{r.def}</span>
              </div>
            ))}
          </div>
          <button
            className="btn roles-start"
            onClick={start}
            disabled={poolSize < 4}
          >
            o open! · spin the roulette
          </button>
          <div className="roles-poolnote">
            playing with {poolSize} of your words
          </div>
        </div>
      </div>
    );
  }

  // ---- done ----------------------------------------------------------------
  if (finished) {
    const perfect = correctCount === round.length;
    return (
      <div className="roles-lab">
        <div className="roles-intro">
          <div className="trophy">
            <Icon name={perfect ? "trophy" : "check"} />
          </div>
          <h2>{perfect ? "nimi ale li pona tawa sina a!" : "pona!"}</h2>
          <p>
            {correctCount} / {round.length} hats spotted
            {perfect ? " — a perfect spin." : "."}
          </p>
          <button className="btn roles-start" onClick={start}>
            o sin! · spin again
          </button>
        </div>
      </div>
    );
  }

  // ---- question ------------------------------------------------------------
  const progressPct = Math.round((qi / round.length) * 100);

  return (
    <div className="roles-lab">
      <div className="roles-progress">
        <div className="roles-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {q.type === "roleSpot" ? (
        <div className="qarea">
          <div className="qkind">which hat is it wearing?</div>
          <Sentence tp={q.tp} word={q.word} />
          <div className="roles-en">{answered ? q.en : " "}</div>
          <div className="choices role-choices">
            {q.options.map((role) => {
              const isCorrect = role === q.correct;
              let cls = "choice";
              if (answered) {
                if (isCorrect) cls += " right";
                else if (role === selection) cls += " wrong";
                else cls += " dim";
              }
              return (
                <button
                  key={role}
                  className={cls}
                  disabled={answered}
                  onClick={() => choose(role, isCorrect)}
                >
                  <span className={`role-badge role-${role}`}>
                    {ROLES[role].label}
                  </span>
                  <span className="role-q">{ROLES[role].q}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="qarea">
          <div className="qkind">
            where is{" "}
            <span className="qword-inline">{q.word}</span> a{" "}
            <span className={`role-badge role-${q.role}`}>
              {ROLES[q.role].label}
            </span>
            ?
          </div>
          <div className="choices role-choices">
            {q.options.map((entry) => {
              const isCorrect = entry.tp === q.correct;
              let cls = "choice role-pick";
              if (answered) {
                if (isCorrect) cls += " right";
                else if (entry.tp === selection) cls += " wrong";
                else cls += " dim";
              }
              return (
                <button
                  key={entry.tp}
                  className={cls}
                  disabled={answered}
                  onClick={() => choose(entry.tp, isCorrect)}
                >
                  <Sentence tp={entry.tp} word={q.word} speak={answered} />
                  {answered && <div className="roles-en small">{entry.en}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {answered && (
        <div className={`foot ${selection === q.correct ? "right" : "wrong"}`}>
          <div className="verdict">
            {selection === q.correct ? "pona!" : "ike a"}
          </div>
          <Html
            className="notes"
            html={
              q.type === "roleSpot"
                ? q.why
                : (q.options.find((o) => o.tp === q.correct) || {}).why || ""
            }
          />
          {WORDS[q.word] && (
            <div className="roles-hatlist">
              <b>{q.word}</b> wears:{" "}
              {rolesForWord(q.word)
                .map((r) => ROLES[r].label)
                .join(" · ")}
            </div>
          )}
          <button className="btn" onClick={advance}>
            {qi + 1 >= round.length ? "finish" : "next"}
          </button>
        </div>
      )}
    </div>
  );
}
